'use client'
import { useContext, useEffect } from 'react'

import clsx from 'clsx'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import { useRouter } from 'next/router'

import { ZenLevitatingStones } from '~/components/zen/ZenLevitatingStones'
import { AuthContext } from '~/context/AuthContext'
import useTrackClick from '~/events/trackers/useTrackClick'
import useTrackScreenView from '~/events/trackers/useTrackScreenView'
import { useCommonTx } from '~/hooks/useTranslationForNamespace'
import CheckIcon from '~/images/icons/SystemOutlined/Checkmark.svg'
import CloseIcon from '~/images/icons/SystemOutlined/Close.svg'
import { Button } from '~/ui/Button/Button'
import Modal from '~/ui/Modal/Modal'
import logger from '~/utils/logger'

const headVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 1.1,
    filter: 'blur(30px)',
  },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const descVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 1.01,
    filter: 'blur(30px)',
  },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const pointsAndButtonVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 1.1,
    filter: 'blur(30px)',
  },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.8,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.18,
      duration: 0.2,
    },
  },
}

const CommonZenModal = ({
  onClose,
  children,
  playAudio,
}: {
  onClose: () => void
  children: React.ReactNode
  playAudio: boolean
}) => {
  useEffect(() => {
    const audio = new Audio('/assets/sounds/zen-intro.mp3')
    if (playAudio && audio) {
      audio.currentTime = 0
      audio.play().catch((err) => logger.error('ZenModal:SoundError:', err))
    }
    return () => {
      if (audio) {
        audio.pause()
      }
    }
  }, [playAudio])

  return (
    <AnimatePresence>
      <Modal
        className={clsx(
          'relative bg-zen-gradient-1 rounded-lg p-6 overflow-y-auto ',
          'w-[100vw] sm:w-full max-w-full sm:max-w-[475px]',
          'min-h-[100dvh] sm:min-h-0 max-h-[100dvh]',
        )}
        isOpen={true}
        onClose={onClose}
      >
        <Button kind='icon' size='none' onClick={onClose} className='absolute right-3 top-3'>
          <CloseIcon title='close-icon' className='w-6 h-6' />
        </Button>
        {children}
      </Modal>
    </AnimatePresence>
  )
}

export const IntroducingZen = ({ onPlayInZenMode, onClose }: { onPlayInZenMode: () => void; onClose: () => void }) => {
  useTrackScreenView({ screen_name: 'introducing_zen_mode' })
  const { trackCta } = useTrackClick()
  const zt = useCommonTx('zen')
  const ct = useCommonTx('common')

  const texts = {
    playGamesInZenMode: ct('playGamesInZenMode'),
    introducing: ct('introducing'),
    zenMode: ct('zenMode'),
    introDesc: zt('introDesc'),
  }

  const _onPlayInZenMode = () => {
    trackCta({
      type: 'button',
      text: texts.playGamesInZenMode,
      click_name: 'introducing_zen_mode_play',
      destination: 'games',
    })
    onPlayInZenMode()
  }

  const _onClose = () => {
    trackCta({
      type: 'button',
      text: 'x',
      click_name: 'introducing_zen_mode_quit',
    })
    onClose()
  }

  return (
    <CommonZenModal onClose={_onClose} playAudio={true}>
      <motion.div
        variants={containerVariants}
        initial='hidden'
        animate='visible'
        className='mx-auto text-center w-full max-w-[332px] flex flex-col items-center justify-center text-text-default'
      >
        <ZenLevitatingStones initialAnimation={true} className='my-10' />

        <motion.div variants={headVariants} className='text-body-text-2 mb-1'>
          {texts.introducing}
        </motion.div>
        <motion.div variants={headVariants} className='text-title-2 font-serif mb-2'>
          {texts.zenMode}
        </motion.div>
        <motion.div variants={descVariants} className='text-body-text-2 mb-6'>
          {texts.introDesc}
        </motion.div>
        <motion.div variants={pointsAndButtonVariants} className='flex flex-col gap-5 w-full text-left mb-7'>
          {[zt('l1'), zt('l2'), zt('l3')].map((text) => {
            return (
              <div className='text-body-text-2 flex items-center gap-2 ' key={text}>
                <div className='w-6 h-6 self-start'>
                  <CheckIcon title='check' className='w-6 h-6' />
                </div>
                {text}
              </div>
            )
          })}
        </motion.div>
        <motion.div variants={pointsAndButtonVariants} className='w-full'>
          <Button className='w-full mb-10' onClick={_onPlayInZenMode}>
            {texts.playGamesInZenMode}
          </Button>
        </motion.div>
      </motion.div>
    </CommonZenModal>
  )
}

export const EnteringZen = ({
  playsLeft,
  onEnteringZen,
  onClose,
}: {
  playsLeft: number
  onEnteringZen: () => void
  onClose: () => void
}) => {
  useTrackScreenView({ screen_name: 'entering_zen_mode' })
  const { trackCta } = useTrackClick()
  const zt = useCommonTx('zen')
  const ct = useCommonTx('common')
  const { hasPremium } = useContext(AuthContext)

  const texts = {
    continue: ct('continue'),
    playsLeft: zt('playsLeft', { playsLeft: playsLeft }),
  }

  const _onClose = () => {
    trackCta({
      type: 'button',
      text: 'x',
      click_name: 'entering_zen_mode_back',
    })
    onClose()
  }

  const _onEnteringZen = () => {
    trackCta({
      type: 'button',
      text: texts.continue,
      click_name: 'entering_zen_mode_continue',
      destination: 'game_player',
    })
    onEnteringZen()
  }

  return (
    <CommonZenModal onClose={_onClose} playAudio={false}>
      <motion.div
        variants={containerVariants}
        initial='hidden'
        animate='visible'
        className='mx-auto text-center w-full max-w-[332px] flex flex-col items-center justify-center text-text-default'
      >
        <ZenLevitatingStones initialAnimation={true} className='my-10' />
        <motion.div variants={headVariants} className='text-title-2 font-serif mb-2'>
          {zt('enterTitle')}
        </motion.div>
        <motion.div variants={descVariants} className='text-body-text-2 mb-6'>
          {zt('enterDesc')}
        </motion.div>
        <motion.div variants={pointsAndButtonVariants} className='flex flex-col gap-5 w-full text-left mb-7'>
          {[zt('l1'), zt('l4'), zt('l5')].map((text) => {
            return (
              <div className='text-body-text-2 flex items-center gap-2 ' key={text}>
                <div className='w-6 h-6 self-start'>
                  <CheckIcon title='check' className='w-6 h-6' />
                </div>
                {text}
              </div>
            )
          })}
        </motion.div>
        <motion.div variants={pointsAndButtonVariants} className='w-full'>
          <Button className='w-full mb-4' onClick={_onEnteringZen}>
            {texts.continue}
          </Button>
        </motion.div>
        {!hasPremium ? (
          <motion.div variants={pointsAndButtonVariants} className='text-body-text-2 mb-8'>
            {texts.playsLeft}
          </motion.div>
        ) : null}
      </motion.div>
    </CommonZenModal>
  )
}

export const ZenLimitReached = ({
  onPlayInRegularMode,
  onClose,
}: {
  onPlayInRegularMode: () => void
  onClose: () => void
}) => {
  useTrackScreenView({ screen_name: 'zen_mode_limit_reached' })
  const { trackCta } = useTrackClick()
  const router = useRouter()

  const zt = useCommonTx('zen')
  const ct = useCommonTx('common')

  const texts = {
    seePremiumPlans: ct('seePremiumPlans'),
    playInRegularMode: ct('playInRegularMode'),
  }

  const onSeePremiumPlans = () => {
    trackCta({
      type: 'button',
      text: texts.seePremiumPlans,
      click_name: 'zen_mode_limit_reached_see_premium_plans',
      destination: 'plan_selection',
    })
    router.push('/subscribe')
  }

  const _onPlayInRegularMode = () => {
    trackCta({
      type: 'button',
      text: texts.playInRegularMode,
      click_name: 'zen_mode_limit_reached_play_in_regular_mode',
      destination: 'game_player',
    })
    onPlayInRegularMode()
  }

  const _onClose = () => {
    trackCta({
      type: 'button',
      text: 'x',
      click_name: 'zen_mode_limit_reached_close',
    })
    onClose()
  }

  return (
    <CommonZenModal onClose={_onClose} playAudio={false}>
      <div className='mx-auto text-center w-full max-w-[332px] flex flex-col items-center justify-center text-text-default'>
        <ZenLevitatingStones initialAnimation={true} className='my-10' />
        <div className='text-title-2 font-serif mb-2'>{zt('limitTitle')}</div>
        <div className='text-body-text-2 mb-6'>{zt('limitDesc')}</div>
        <div className='w-full'>
          <Button kind='primary' className='w-full mb-2' onClick={onSeePremiumPlans}>
            {texts.seePremiumPlans}
          </Button>
        </div>
        <div className='w-full'>
          <Button kind='secondary' className='w-full mb-6' onClick={_onPlayInRegularMode}>
            {texts.playInRegularMode}
          </Button>
        </div>
      </div>
    </CommonZenModal>
  )
}
